'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { MapPin, ArrowRight, User, LogOut } from 'lucide-react';
import { CalenddyLogo } from '@/components/brand/CalenddyLogo';
import { getThemeTokens } from '@/lib/themeTokens';
import { ClientProfilePortalView } from './ClientProfilePortalView';

interface BranchItem {
  id: string;
  name: string;
  slug: string;
  address?: string | null;
  googleMapsUrl?: string | null;
  phone?: string | null;
  email?: string | null;
  imageUrl?: string | null;
  coverUrl?: string | null;
  active: boolean;
  isMain?: boolean;
}

interface BusinessData {
  id: string;
  name: string;
  slug: string;
  category?: string | null;
  description?: string | null;
  brandColor?: string | null;
  logoUrl?: string | null;
  coverUrl?: string | null;
  portalTheme?: string | null;
  branches: BranchItem[];
}

interface BrandHubViewProps {
  business: BusinessData;
  onOpenMyBookings?: () => void;
}

export function BrandHubView({ business, onOpenMyBookings }: BrandHubViewProps) {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const theme = getThemeTokens(business.portalTheme, business.brandColor);
  const initialLetter = business.name.trim().charAt(0).toUpperCase() || 'C';
  const activeBranches = (business.branches || []).filter(b => b.active);

  if (isProfileOpen) {
    return (
      <ClientProfilePortalView
        business={business}
        currentBranch={business.branches?.[0]}
        onBackToCatalog={() => setIsProfileOpen(false)}
        onRescheduleFromProfile={(booking) => {
          setIsProfileOpen(false);
          const branchSlug = booking.branchSlug || business.branches?.[0]?.slug || '';
          window.location.href = `/${business.slug}/${branchSlug}`;
        }}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col font-jakarta bg-white text-[#111827] selection:bg-slate-200">
      {/* 1. Header Superior Discreto y Limpio (Fiel a Calendico Cerini) */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100 px-4 sm:px-8 md:px-12 py-3.5">
        <nav className="max-w-[1240px] mx-auto flex items-center justify-between" aria-label="Global">
          {/* Logo Plataforma Calenddy */}
          <div className="flex items-center gap-2">
            <CalenddyLogo href="/" size="sm" variant="dark" />
          </div>

          {/* Lado Derecho: Botón Mi perfil + Botón Cerrar sesión (Idéntico a Imagen 2) */}
          <div className="flex items-center gap-2">
            <button
              onClick={onOpenMyBookings || (() => setIsProfileOpen(true))}
              style={{
                fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                fontSize: '14px',
                fontWeight: 500
              }}
              className="bg-[#111827] hover:bg-black text-white h-10 px-3.5 rounded-xl flex items-center gap-2 transition cursor-pointer shadow-xs whitespace-nowrap"
            >
              <User className="w-4 h-4 text-white" />
              <span>Mi perfil</span>
            </button>

            <button
              onClick={() => {
                window.location.href = '/';
              }}
              className="h-10 px-3 sm:px-3.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 flex items-center gap-2 text-gray-700 transition cursor-pointer shadow-2xs shrink-0"
              title="Cerrar sesión"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline text-sm font-medium">Cerrar sesión</span>
            </button>
          </div>
        </nav>
      </header>

      {/* 2. Portada (Proporción exacta Calendico: h-52 sm:h-64 md:h-76) */}
      <div className="relative w-full overflow-hidden bg-gray-100">
        {business.coverUrl ? (
          <div className="h-52 sm:h-64 md:h-76 w-full relative">
            <img
              src={business.coverUrl}
              alt={business.name}
              className="w-full h-full object-cover object-center"
            />
          </div>
        ) : (
          <div className="h-44 sm:h-56 md:h-64 w-full relative bg-gradient-to-r from-gray-100 to-gray-200 border-b border-gray-100" />
        )}
      </div>

      {/* 3. Hoja Blanca Unificada: Superpuesta a la portada con aire lateral reducido a la mitad (~28px en 1366px) */}
      <main className="max-w-[1310px] mx-auto w-full relative -mt-5 sm:-mt-6 z-10 bg-white rounded-t-3xl px-4 sm:px-6 lg:px-8 pb-16 flex-1">
        {/* Cabecera de Marca: Logo badge + Nombre + Subtítulo naturalmente centrados en el eje vertical */}
        <div className="pt-4 sm:pt-6 pb-6">
          <div className="flex items-center space-x-4 sm:space-x-6">
            <div className="shrink-0">
              {business.logoUrl ? (
                <img
                  src={business.logoUrl}
                  alt={business.name}
                  className="h-24 w-24 sm:h-32 sm:w-32 md:h-36 md:w-36 rounded-3xl object-cover object-center bg-black text-white ring-4 ring-white shadow-sm"
                />
              ) : (
                <div
                  style={{
                    backgroundColor: theme.brandButtonBg,
                    color: theme.brandButtonText
                  }}
                  className="h-24 w-24 sm:h-32 sm:w-32 md:h-36 md:w-36 rounded-3xl ring-4 ring-white shadow-sm font-bold text-2xl sm:text-4xl flex items-center justify-center select-none"
                >
                  {initialLetter}
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1 flex flex-col justify-center text-left py-1">
              <h1
                style={{
                  fontFamily: '"Plus Jakarta Display", "Inter var", ui-sans-serif, system-ui, sans-serif',
                  fontSize: '28px',
                  fontWeight: 600,
                  lineHeight: '34px',
                  color: '#111827'
                }}
                className="tracking-normal truncate"
              >
                {business.name}
              </h1>
              <p
                style={{
                  fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                  fontSize: '15px',
                  fontWeight: 400,
                  lineHeight: '22px',
                  color: '#6b7280'
                }}
                className="mt-0.5 line-clamp-2"
              >
                {business.description || 'Selecciona el salón que gustes, y reservá tu turno'}
              </p>
            </div>
          </div>
        </div>

        {/* 4. Sección de Sucursales: 2 columnas en móvil, 4 columnas en desktop (exacto Cerini) */}
        <div className="space-y-4 pt-2">
          <div className="flex items-center justify-between mb-2">
            <h2
              style={{
                fontFamily: '"Plus Jakarta Display", "Inter var", ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                fontSize: '20px',
                fontWeight: 500,
                lineHeight: '28px',
                color: '#111827'
              }}
            >
              Nuestras sucursales
            </h2>
            <span
              style={{
                fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                fontSize: '14px',
                color: '#374151'
              }}
              className="hidden sm:inline-block"
            >
              {activeBranches.length} {activeBranches.length === 1 ? 'sucursal' : 'sucursales'}
            </span>
          </div>

          {activeBranches.length === 0 ? (
            <div className="rounded-[16px] border border-gray-200 p-12 text-center max-w-md mx-auto bg-white">
              <MapPin className="w-10 h-10 mx-auto mb-3 text-gray-400" />
              <h3
                style={{
                  fontFamily: '"Plus Jakarta Display", "Inter var", ui-sans-serif, system-ui, sans-serif',
                  fontSize: '16px',
                  fontWeight: 500,
                  color: '#111827'
                }}
                className="mb-1"
              >
                No hay sucursales activas
              </h3>
              <p
                style={{
                  fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                  fontSize: '13px',
                  color: '#6b7280'
                }}
              >
                Este negocio no cuenta con sedes públicas para reserva en este momento.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 w-full">
              {activeBranches.map((branch) => (
                <div
                  key={branch.id}
                  className="relative flex flex-col overflow-hidden border border-gray-200 rounded-[16px] hover:bg-gray-50/50 bg-white transition-all duration-200 ease-out hover:shadow-md hover:-translate-y-1 cursor-pointer group w-full shadow-2xs"
                >
                  <Link
                    href={`/${business.slug}/${branch.slug}`}
                    className="flex flex-col h-full w-full"
                  >
                    {/* Fotografía de la Sucursal: h-28 en móvil, h-36/40 en tablet/desktop */}
                    <div className="h-28 sm:h-36 md:h-40 overflow-hidden flex items-center justify-center shrink-0 bg-gray-100 w-full">
                      {branch.imageUrl ? (
                        <img
                          src={branch.imageUrl}
                          alt={branch.name}
                          className="w-full h-full object-cover object-center"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                          <MapPin className="w-8 h-8" />
                        </div>
                      )}
                    </div>

                    {/* Contenido de la Tarjeta */}
                    <div className="p-3.5 sm:p-4 text-left flex flex-col justify-between flex-1">
                      <div>
                        <h3
                          style={{
                            fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                            fontSize: '16px',
                            fontWeight: 500,
                            lineHeight: '22px',
                            color: '#111827'
                          }}
                          className="line-clamp-1 group-hover:text-gray-700"
                        >
                          {branch.name}
                        </h3>

                        <p
                          style={{
                            fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                            fontSize: '13px',
                            lineHeight: '18px',
                            color: '#374151'
                          }}
                          className="line-clamp-2 mt-1 mb-2 sm:mb-3 min-h-[2rem] sm:min-h-0"
                        >
                          {branch.address || `${business.name} ${branch.name}`}
                        </p>
                      </div>

                      {/* Acción Directa con divisor superior: Ir a sucursal → */}
                      <div className="border-t border-gray-100 pt-2 sm:pt-2.5 mt-auto">
                        <div
                          style={{
                            fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#00966b'
                          }}
                          className="flex items-center gap-1.5 group-hover:opacity-80 transition-opacity"
                        >
                          <span>Ir a sucursal</span>
                          <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-0.5 transform duration-150" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* 5. Footer Discreto */}
      <footer className="w-full border-t border-gray-100 bg-white py-6 px-4 text-center text-xs text-gray-500 mt-auto">
        <div className="max-w-[1310px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-6">
          <p>© {new Date().getFullYear()} {business.name}. Todos los derechos reservados.</p>
          <div className="flex items-center gap-1.5">
            <span>Powered by</span>
            <CalenddyLogo size="xs" variant="dark" />
          </div>
        </div>
      </footer>
    </div>
  );
}
